import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Users, MapPin, Navigation, Share2, Copy, CheckCircle2,
  Loader2, ArrowRight, Sparkles, DollarSign, XCircle, LogOut, UserCheck,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SavePlaceChip from "../components/SavePlaceChip";

/**
 * SharedRide — group booking for taxis.
 * ─────────────────────────────────────
 * One passenger fills the form → gets an SR-XXXXXX share code → friends
 * open the link, sign in, and tap Join. Fare per seat auto-scales down as
 * more friends join (2 pax = 65% each, 3 = 43%, 4 = 32.5%).
 *
 * Routes:
 *  /shared         → create form
 *  /shared/:code   → join/view page
 */
export default function SharedRide() {
  const params = useParams();
  const code = (params.code || "").toUpperCase();
  return code ? <SharedRideDetail code={code} /> : <SharedRideCreate />;
}

// ─── Create screen ──────────────────────────────────────────────────
function SharedRideCreate() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pickup, setPickup] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null); // {lat,lng}
  const [dropoff, setDropoff] = useState("");
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [maxPax, setMaxPax] = useState(4);
  const [notes, setNotes] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const refreshSaved = () => api.get("/favorites").then((r) => setSavedPlaces(r.data || [])).catch(() => {});
  useEffect(() => { if (user) refreshSaved(); }, [user]);

  // Prefill from optional query params so we can deep-link into this page
  // (e.g., from the Estimate flow).
  useEffect(() => {
    const p = searchParams.get("pickup");
    const d = searchParams.get("dropoff");
    if (p) setPickup(p);
    if (d) setDropoff(d);
  }, [searchParams]);

  const geocode = async (query) => {
    if (!query || query.length < 3) return null;
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
      const res = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await res.json();
      if (data?.[0]) return { lat: Number(data[0].lat), lng: Number(data[0].lon), display: data[0].display_name };
    } catch (e) {
      /* silent */
    }
    return null;
  };

  const runQuote = async () => {
    setQuoteBusy(true);
    setQuote(null);
    try {
      const [pc, dc] = await Promise.all([
        pickupCoords || geocode(pickup),
        dropoffCoords || geocode(dropoff),
      ]);
      if (!pc || !dc) {
        toast.error(t("shared.errAddressLookup", "Could not find one of the addresses"));
        return;
      }
      setPickupCoords(pc);
      setDropoffCoords(dc);
      const res = await api.post("/shared-rides/quote", {
        pickup_lat: pc.lat, pickup_lng: pc.lng,
        dropoff_lat: dc.lat, dropoff_lng: dc.lng,
        category: "taxi",
        max_passengers: maxPax,
      });
      setQuote(res.data.quote);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQuoteBusy(false);
    }
  };

  // Auto-quote whenever both endpoints have coords + seat count changes.
  // Matches Uber's UX where the fare updates without an extra tap.
  useEffect(() => {
    if (!pickupCoords || !dropoffCoords) return;
    let cancelled = false;
    setQuoteBusy(true);
    (async () => {
      try {
        const res = await api.post("/shared-rides/quote", {
          pickup_lat: pickupCoords.lat, pickup_lng: pickupCoords.lng,
          dropoff_lat: dropoffCoords.lat, dropoff_lng: dropoffCoords.lng,
          category: "taxi",
          max_passengers: maxPax,
        });
        if (!cancelled) setQuote(res.data.quote);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setQuoteBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickupCoords, dropoffCoords, maxPax, t]);

  const submit = async () => {
    if (!pickupCoords || !dropoffCoords) {
      toast.error(t("shared.errNoQuote", "Get an estimate first"));
      return;
    }    setSubmitBusy(true);
    try {
      const res = await api.post("/shared-rides", {
        pickup_lat: pickupCoords.lat, pickup_lng: pickupCoords.lng,
        pickup_address: pickup,
        dropoff_lat: dropoffCoords.lat, dropoff_lng: dropoffCoords.lng,
        dropoff_address: dropoff,
        category: "taxi",
        max_passengers: maxPax,
        notes: notes.trim() || undefined,
      });
      const code = res.data.shared_ride.share_code;
      toast.success(t("shared.created", "Shared ride created — invite your friends!"));
      navigate(`/shared/${code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-black text-white">{t("shared.signInFirst", "Sign in to start a shared ride")}</h1>
        <Link to="/login" className="mt-6 inline-block bg-electric-teal text-obsidian rounded-full px-6 py-3 text-sm font-black">
          {t("nav.signIn", "Sign In")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[720px] mx-auto px-4 py-6" data-testid="shared-ride-create">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-full bg-electric-teal text-obsidian flex items-center justify-center">
          <Users size={20} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">
            {t("shared.eyebrow", "Ride together, pay less")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white">
            {t("shared.title", "Start a shared ride")}
          </h1>
        </div>
      </div>

      <p className="text-sm text-white/60 leading-relaxed mb-6">
        {t(
          "shared.subtitle",
          "Book a taxi, invite up to 3 friends. Everyone pays less — the more people join, the cheaper each seat gets."
        )}
      </p>

      {/* Form */}
      <div className="rounded-2xl border border-white/10 bg-obsidian/60 p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase font-black tracking-widest text-white/40 inline-flex items-center gap-1.5 mb-1">
            <MapPin size={12} className="text-electric-teal" /> {t("shared.pickup", "Pickup address")}
          </div>
          <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 focus-within:border-electric-teal">
            <AddressAutocomplete
              value={pickup}
              onChange={(v) => { setPickup(v); setPickupCoords(null); setQuote(null); }}
              onSelect={(sel) => { setPickup(sel.address); setPickupCoords({ lat: sel.lat, lng: sel.lng }); setQuote(null); }}
              placeholder={t("shared.pickupPh", "Where do you start?")}
              testid="shared-pickup"
              hasCoords={!!pickupCoords}
              savedPlaces={savedPlaces}
              trailingSlot={pickupCoords && (
                <SavePlaceChip
                  address={pickup}
                  lat={pickupCoords.lat}
                  lng={pickupCoords.lng}
                  existing={savedPlaces}
                  onSaved={refreshSaved}
                  testid="save-shared-pickup"
                />
              )}
            />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black tracking-widest text-white/40 inline-flex items-center gap-1.5 mb-1">
            <Navigation size={12} className="text-electric-teal" /> {t("shared.dropoff", "Drop-off address")}
          </div>
          <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 focus-within:border-electric-teal">
            <AddressAutocomplete
              value={dropoff}
              onChange={(v) => { setDropoff(v); setDropoffCoords(null); setQuote(null); }}
              onSelect={(sel) => { setDropoff(sel.address); setDropoffCoords({ lat: sel.lat, lng: sel.lng }); setQuote(null); }}
              placeholder={t("shared.dropoffPh", "Where are you heading?")}
              testid="shared-dropoff"
              hasCoords={!!dropoffCoords}
              savedPlaces={savedPlaces}
              trailingSlot={dropoffCoords && (
                <SavePlaceChip
                  address={dropoff}
                  lat={dropoffCoords.lat}
                  lng={dropoffCoords.lng}
                  existing={savedPlaces}
                  onSaved={refreshSaved}
                  testid="save-shared-dropoff"
                />
              )}
            />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black tracking-widest text-white/40 mb-2">
            {t("shared.maxPax", "Total seats (including you)")}
          </div>
          <div className="flex gap-2">
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => { setMaxPax(n); setQuote(null); }}
                className={`flex-1 rounded-full py-2.5 text-sm font-black border transition ${
                  maxPax === n
                    ? "bg-electric-teal text-obsidian border-electric-teal"
                    : "bg-white/5 text-white border-white/10 hover:bg-white/10"
                }`}
                data-testid={`shared-pax-${n}`}
              >
                {n} {n === 1 ? t("shared.seat", "seat") : t("shared.seats", "seats")}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black tracking-widest text-white/40 mb-2">
            {t("shared.notesLabel", "Notes for riders (optional)")}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={280}
            rows={2}
            placeholder={t("shared.notesPh", "e.g. Airport run @ 6am — bring ID")}
            className="w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-electric-teal resize-none"
            data-testid="shared-notes"
          />
        </div>

        {quoteBusy && !quote && (
          <div className="rounded-xl border border-electric-teal/20 bg-electric-teal/[0.04] p-4 text-xs text-electric-teal inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> {t("shared.calculating", "Calculating your savings…")}
          </div>
        )}

        {quote && <QuoteBreakdown quote={quote} maxPax={maxPax} />}

        <button
          onClick={submit}
          disabled={!quote || submitBusy}
          className="w-full rounded-full bg-electric-teal hover:bg-electric-teal/90 transition text-obsidian px-4 py-3 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="shared-create-btn"
        >
          {submitBusy && <Loader2 size={14} className="animate-spin" />}
          {t("shared.create", "Create shared ride")} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function QuoteBreakdown({ quote, maxPax }) {
  const { t } = useTranslation();
  const perSeat = quote.per_seat_by_pax?.[String(maxPax)];
  const solo = quote.solo_fare || 0;
  const savings = solo > 0 && perSeat ? Math.round(((solo - perSeat) / solo) * 100) : 0;
  const currency = (quote.currency || "USD").toUpperCase();
  return (
    <div className="rounded-xl border border-electric-teal/30 bg-electric-teal/[0.06] p-4" data-testid="shared-quote-breakdown">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-white/40">
            {t("shared.yourSeat", "Your seat (if {{n}} join)", { n: maxPax })}
          </div>
          <div className="text-3xl sm:text-4xl font-black text-electric-teal tabular-nums" data-testid="shared-per-seat-price">
            {perSeat?.toFixed(0)} {currency}
          </div>
        </div>
        <div className="text-end">
          <div className="text-[10px] uppercase tracking-widest font-black text-white/40">
            {t("shared.soloFare", "Solo fare")}
          </div>
          <div className="text-lg font-black text-white/70 line-through tabular-nums">
            {solo.toFixed(0)} {currency}
          </div>
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 px-3 py-1 text-[10px] uppercase font-black tracking-widest">
        <DollarSign size={12} /> {t("shared.saveN", "Save {{n}}% each", { n: savings })}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] text-white/50">
        {[2, 3, 4].map((n) => (
          <div
            key={n}
            className={`rounded-lg border px-2 py-1.5 text-center ${
              n === maxPax ? "border-electric-teal/60 bg-electric-teal/10 text-electric-teal" : "border-white/10 bg-white/5"
            }`}
          >
            <div className="font-black">{n} pax</div>
            <div className="tabular-nums">{quote.per_seat_by_pax?.[String(n)]?.toFixed(0)} {currency}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ icon, label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase font-black tracking-widest text-white/40 inline-flex items-center gap-1.5">
        {icon} {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-electric-teal"
        data-testid={testid}
      />
    </label>
  );
}

// ─── Detail / Join screen ────────────────────────────────────────────
function SharedRideDetail({ code }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/shared-rides/${code}`);
      setRide(res.data.shared_ride);
    } catch (err) {
      setRide(null);
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const shareUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/shared/${code}`;
  }, [code]);

  const buildShareText = () =>
    t(
      "shared.inviteText",
      "Join my Drivons shared taxi from {{from}} to {{to}} — just {{price}} {{currency}} per seat if we're {{pax}}. Tap: {{url}}",
      {
        from: ride?.pickup?.address || t("shared.pickup", "pickup"),
        to: ride?.dropoff?.address || t("shared.dropoff", "drop-off"),
        price: ride?.per_seat_estimate?.toFixed(0) || "?",
        currency: (ride?.currency || "").toUpperCase(),
        pax: ride?.max_passengers || 4,
        url: shareUrl,
      }
    );

  const handleShare = async () => {
    const text = buildShareText();
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: t("shared.shareTitle", "Ride together on Drivons"),
          text,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("shared.linkCopied", "Invite copied — paste it anywhere"));
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error(t("shared.shareFailed", "Could not open share sheet"));
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t("shared.codeCopied", "Code copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error(t("shared.copyFailed", "Could not copy code"));
    }
  };

  const handleJoin = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/shared/${code}`)}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/shared-rides/${code}/join`);
      toast.success(t("shared.joined", "You're in! See you at the pickup."));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    try {
      await api.post(`/shared-rides/${code}/leave`);
      toast.success(t("shared.left", "You left this shared ride"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("shared.confirmCancel", "Cancel this shared ride for everyone?"))) return;
    setBusy(true);
    try {
      await api.post(`/shared-rides/${code}/cancel`);
      toast.success(t("shared.cancelled", "Shared ride cancelled"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 flex justify-center">
        <Loader2 className="animate-spin text-electric-teal" size={24} />
      </div>
    );
  }
  if (!ride) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <div className="text-4xl mb-2">🔍</div>
        <h1 className="text-2xl font-black text-white">{t("shared.notFound", "Shared ride not found")}</h1>
        <p className="text-sm text-white/60 mt-2">
          {t("shared.notFoundHint", "The code may have expired. Ask the creator for a new one.")}
        </p>
        <Link to="/shared" className="mt-6 inline-block bg-electric-teal text-obsidian rounded-full px-5 py-2.5 text-xs font-black">
          {t("shared.createOwn", "Create your own shared ride")}
        </Link>
      </div>
    );
  }

  const isFull = ride.seats_left === 0;
  const isCancelled = ride.status === "cancelled";
  const canJoin = ride.status === "open" && !ride.viewer_is_participant && !isFull;

  return (
    <div className="max-w-[720px] mx-auto px-4 py-6" data-testid="shared-ride-detail">
      {/* Hero: big code + share */}
      <div className="rounded-2xl border border-electric-teal/30 bg-gradient-to-br from-electric-teal/20 via-obsidian to-obsidian p-5 sm:p-6 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-electric-teal text-obsidian flex items-center justify-center">
            <Users size={18} strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">
              {t("shared.badge", "Shared taxi ride")}
            </div>
            <div className="text-sm font-black text-white">
              {t("shared.byCreator", "Started by {{name}}", { name: ride.creator_name || "a rider" })}
            </div>
          </div>
          <span
            className={`ms-auto text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded-full ${
              isCancelled ? "bg-red-500/15 text-red-300" :
              ride.status === "confirmed" ? "bg-blue-500/15 text-blue-300" :
              "bg-emerald-500/15 text-emerald-300"
            }`}
            data-testid="shared-status"
          >
            {t(`shared.status.${ride.status}`, ride.status)}
          </span>
        </div>

        <div className="mt-4 rounded-xl border border-electric-teal/40 bg-obsidian/70 p-4 backdrop-blur-sm">
          <div className="text-[10px] uppercase tracking-widest font-black text-white/40">{t("shared.rideCode", "Ride code")}</div>
          <div className="mt-1 font-black text-white tabular-nums tracking-[0.15em] text-3xl sm:text-4xl select-all" data-testid="shared-code">
            {ride.share_code}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={handleCopyCode}
            className="rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition text-white px-4 py-3 text-xs font-black inline-flex items-center justify-center gap-2"
            data-testid="shared-copy-btn"
          >
            {copied ? <><CheckCircle2 size={14} className="text-electric-teal" /> {t("shared.copied", "Copied")}</> : <><Copy size={14} /> {t("shared.copyCode", "Copy code")}</>}
          </button>
          <button
            onClick={handleShare}
            className="rounded-full bg-electric-teal hover:bg-electric-teal/90 transition text-obsidian px-4 py-3 text-xs font-black inline-flex items-center justify-center gap-2"
            data-testid="shared-share-btn"
          >
            <Share2 size={14} strokeWidth={2.5} /> {t("shared.invite", "Invite friends")}
          </button>
        </div>
      </div>

      {/* Route card */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-obsidian/60 p-5" data-testid="shared-route-card">
        <RouteLine label={t("shared.pickup", "Pickup")} address={ride.pickup?.address} icon={<MapPin size={14} className="text-electric-teal" />} />
        <div className="my-3 border-s-2 border-dashed border-white/15 h-6 ms-2" />
        <RouteLine label={t("shared.dropoff", "Drop-off")} address={ride.dropoff?.address} icon={<Navigation size={14} className="text-electric-teal" />} />
        {ride.notes && (
          <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 text-xs text-white/70">
            <div className="text-[10px] uppercase font-black tracking-widest text-white/40 mb-1">{t("shared.notes", "Notes")}</div>
            {ride.notes}
          </div>
        )}
      </div>

      {/* Fare */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-obsidian/60 p-5" data-testid="shared-fare-card">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40">{t("shared.yourSeatShort", "Your seat")}</div>
            <div className="text-3xl sm:text-4xl font-black text-electric-teal tabular-nums" data-testid="shared-detail-per-seat">
              {ride.per_seat_estimate?.toFixed(0)} {(ride.currency || "").toUpperCase()}
            </div>
          </div>
          <div className="text-end">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40">{t("shared.soloFare", "Solo fare")}</div>
            <div className="text-lg font-black text-white/70 line-through tabular-nums">
              {ride.solo_fare?.toFixed(0)} {(ride.currency || "").toUpperCase()}
            </div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-white/60">
          {t("shared.fareHint", "Final split adjusts based on how many riders actually board.")}
        </div>
      </div>

      {/* Passengers */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-obsidian/60 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest font-black text-white/40 inline-flex items-center gap-2">
            <Users size={12} /> {t("shared.riders", "Riders")} · {ride.passenger_count}/{ride.max_passengers}
          </div>
          <div className="text-[10px] text-white/50 tabular-nums" data-testid="shared-seats-left">
            {ride.seats_left > 0
              ? t("shared.seatsLeft", "{{n}} seats left", { n: ride.seats_left })
              : t("shared.full", "Full")}
          </div>
        </div>
        <div className="space-y-2">
          {ride.participants.map((p) => (
            <div
              key={p.user_id}
              className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 p-3"
              data-testid={`shared-participant-${p.user_id}`}
            >
              <div className="w-8 h-8 rounded-full bg-electric-teal/20 text-electric-teal flex items-center justify-center font-black text-xs">
                {(p.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-white truncate">{p.name || t("shared.anon", "Rider")}</div>
                {p.is_creator && (
                  <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal inline-flex items-center gap-1">
                    <UserCheck size={10} /> {t("shared.creator", "Creator")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 grid gap-2">
        {canJoin && (
          <button
            onClick={handleJoin}
            disabled={busy}
            className="w-full rounded-full bg-electric-teal hover:bg-electric-teal/90 transition text-obsidian px-4 py-3 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="shared-join-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
            {t("shared.joinCta", "Join this ride")}
          </button>
        )}
        {ride.viewer_is_participant && !ride.viewer_is_creator && ride.status === "open" && (
          <button
            onClick={handleLeave}
            disabled={busy}
            className="w-full rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition text-white px-4 py-3 text-xs font-black inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="shared-leave-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            {t("shared.leaveCta", "Leave shared ride")}
          </button>
        )}
        {ride.viewer_is_creator && ride.status === "open" && (
          <button
            onClick={handleCancel}
            disabled={busy}
            className="w-full rounded-full border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition text-red-300 px-4 py-3 text-xs font-black inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="shared-cancel-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            {t("shared.cancelCta", "Cancel shared ride")}
          </button>
        )}
        {!user && (
          <Link
            to={`/login?next=${encodeURIComponent(`/shared/${code}`)}`}
            className="w-full rounded-full bg-electric-teal hover:bg-electric-teal/90 transition text-obsidian px-4 py-3 text-sm font-black inline-flex items-center justify-center gap-2"
            data-testid="shared-signin-to-join"
          >
            <UserCheck size={14} /> {t("shared.signInToJoin", "Sign in to join")}
          </Link>
        )}
      </div>
    </div>
  );
}

function RouteLine({ label, address, icon }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-electric-teal/15 flex items-center justify-center shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-black text-white/40">{label}</div>
        <div className="text-sm font-bold text-white break-words">{address || "—"}</div>
      </div>
    </div>
  );
}
